import type { EditHistoryState, Operation } from '@fit-editor/fit-core'

/**
 * Surfaces the time ranges affected by already-*committed* edit operations
 * (`history.log[0, cursor)`), for the timeline's "edited-range
 * highlighting" requirement -- distinct from `selectedRangeMs` (a
 * not-yet-applied selection) and from pauses (not an edit at all).
 *
 * Only operations with a genuinely renderable sub-interval are included.
 * `crop` is deliberately excluded: the records outside its kept range are
 * gone from `currentActivity`'s replay, so there is nothing left in the
 * chart's data domain to highlight -- the cropped-away region isn't a
 * "this part was edited" annotation, it just no longer exists.
 * `shiftTime`/`editMetadata`/`merge` have no single meaningful sub-range
 * and are excluded for the same reason. `setElevation` is annotated over
 * its explicit range, or (whole-activity scope) the span of its samples.
 */
export interface EditedRange {
  start: number
  end: number
  label: string
}

const OPERATION_LABELS: Record<string, string> = {
  removeInterval: 'Removed interval',
  removeGpsOnly: 'GPS removed',
  setGpsConstant: 'GPS set constant',
  interpolateGps: 'GPS interpolated',
  repairOutlier: 'GPS point repaired',
  setElevation: 'Terrain elevation applied',
}

function operationRange(op: Operation): { start: number; end: number } | null {
  switch (op.type) {
    case 'removeInterval':
    case 'removeGpsOnly':
    case 'setGpsConstant':
    case 'interpolateGps':
      return { start: op.start.getTime(), end: op.end.getTime() }
    case 'repairOutlier':
      return { start: op.timestamp.getTime(), end: op.timestamp.getTime() }
    case 'setElevation': {
      if (op.start != null && op.end != null) {
        return { start: op.start.getTime(), end: op.end.getTime() }
      }
      // Whole-activity scope: the span of the samples actually embedded.
      let start = Infinity
      let end = -Infinity
      for (const sample of op.samples) {
        if (sample.timestampMs < start) start = sample.timestampMs
        if (sample.timestampMs > end) end = sample.timestampMs
      }
      return Number.isFinite(start) ? { start, end } : null
    }
    default:
      return null
  }
}

export function getEditedRanges(history: EditHistoryState): EditedRange[] {
  const applied = history.log.slice(0, history.cursor)
  const ranges: EditedRange[] = []
  applied.forEach((op, i) => {
    const range = operationRange(op)
    if (range == null) return
    const label = OPERATION_LABELS[op.type] ?? op.type
    ranges.push({ ...range, label: `Edited: ${label} #${i + 1}` })
  })
  return ranges
}
